import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LinkEditor } from "@/components/platform/LinkEditor";
import { api } from "@/lib/api";
import { apiError } from "@/lib/platform";

const PRIORITIES = ["Low", "Medium", "High", "Critical"];
const blank = { title: "", description: "", owner: "", due_date: "", priority: "Medium", status: "Open", links: [] };

export const TaskFormDialog = ({ open, onOpenChange, task, onSaved }) => {
  const [form, setForm] = useState(blank);
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(task ? { ...blank, ...task, owner: task.owner || "", due_date: task.due_date || "" } : blank);
    api.get("/platform/users/directory").then(({ data }) => setUsers(data.users)).catch(() => {});
  }, [open, task]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

  const save = async () => {
    setSaving(true);
    const payload = {
      title: form.title, description: form.description, owner: form.owner || null,
      due_date: form.due_date || null, priority: form.priority,
      links: form.links.map(({ entity_type, entity_id, label }) => ({ entity_type, entity_id, label })),
    };
    try {
      const { data } = task
        ? await api.put(`/platform/tasks/${task.id}`, payload)
        : await api.post("/platform/tasks", { ...payload, status: form.status });
      toast.success(task ? "Task updated" : `Task ${data.task_ref} created`);
      onSaved?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(apiError(e, "Could not save task"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="task-form-dialog">
        <DialogHeader>
          <DialogTitle>{task ? `Edit ${task.task_ref}` : "New task"}</DialogTitle>
          <DialogDescription>Tasks can be linked to vendors, incidents, requests, controls or retention batches.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Task title</Label>
            <Input value={form.title} onChange={set("title")} data-testid="task-form-title" placeholder="e.g. Collect signed DPA from payroll vendor" />
          </div>
          <div className="space-y-1.5">
            <Label>Description</Label>
            <Textarea rows={3} value={form.description} onChange={set("description")} data-testid="task-form-description" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Owner</Label>
              <Select value={form.owner || "__none"} onValueChange={(v) => set("owner")(v === "__none" ? "" : v)}>
                <SelectTrigger data-testid="task-form-owner"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">Unassigned</SelectItem>
                  {users.map((u) => <SelectItem key={u.username} value={u.username}>{u.full_name || u.username}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Due date</Label>
              <Input type="date" value={form.due_date} onChange={set("due_date")} data-testid="task-form-due-date" />
            </div>
            <div className="space-y-1.5">
              <Label>Priority</Label>
              <Select value={form.priority} onValueChange={set("priority")}>
                <SelectTrigger data-testid="task-form-priority"><SelectValue /></SelectTrigger>
                <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {!task && (
              <div className="space-y-1.5">
                <Label>Initial status</Label>
                <Select value={form.status} onValueChange={set("status")}>
                  <SelectTrigger data-testid="task-form-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Open">Open</SelectItem>
                    <SelectItem value="Draft">Draft</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <div className="space-y-1.5">
            <Label>Linked records</Label>
            <LinkEditor links={form.links} onChange={set("links")} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="task-form-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving || !form.title.trim()} className="bg-[#003366] hover:bg-[#003366]/90"
                  data-testid="task-form-submit">
            {saving ? "Saving…" : task ? "Save changes" : "Create task"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
